// What a mesh lamp's two sliders MEAN, and how they become mesh messages.
// Pure — tested in meshValues.test.js.
//
// The LampSmart lamps take two raw channels (cold, warm). A SIG Mesh lamp takes
// LIGHTNESS (0..65535) and a colour TEMPERATURE in kelvin, within the range it
// reports at configure time (Light CTL Temperature Range). So a mesh node's
// LastValues is "brightness,coolness", both 0..255 on the same string format
// the server already stores:
//
//   brightness  0 = off … 255 = full
//   coolness    0 = warmest the lamp can do … 255 = coolest
//
// ⚠️ SAME COLUMN, DIFFERENT MEANING. LastValues on a LampSmart node is
// "white,warm"; on a mesh node (Codec 'sig_mesh') it is "brightness,coolness".
// A node's whole tree is one family (the server refuses to mix them), so the
// meaning is fixed per screen — but never parse one as the other.

export const MESH_CODEC = 'sig_mesh';
export const isMeshCodec = (codec) => String(codec || '') === MESH_CODEC;

/** The spec's full range, used only until the lamp has reported its own. */
export const DEFAULT_TEMP_RANGE = { min: 800, max: 20000 };

/**
 * Warm White · Cool White · White · Off — as "brightness,coolness".
 *
 * ⚠️ THE ORDER OF THE NAMES IS THE CUSTOMER'S, CHECKED ON THE LAMPS (2026-10-11):
 * the middle temperature is called COOL WHITE and the coolest end WHITE — the
 * opposite of the first labels. Only the words moved; what each button sends
 * (and its swatch) is unchanged. Do not "correct" this back.
 */
export const MESH_PRESETS = [
  { label: 'Warm\nWhite', command: '255,0', swatch: '#FFD08A' },
  { label: 'Cool\nWhite', command: '255,128', swatch: '#FFF6E5' },
  { label: 'White', command: '255,255', swatch: '#E8F1FF' },
  { label: 'Off', command: '0,128', swatch: '#000000' },
];

const clamp255 = (v) => Math.max(0, Math.min(255, Math.round(Number(v) || 0)));

/** 0..255 -> Light Lightness 0..65535. */
export const toLightness = (brightness) => Math.round((clamp255(brightness) / 255) * 0xffff);

/** Light Lightness 0..65535 -> 0..255 (what the lamp REPORTS, back to slider units). */
export const fromLightness = (lightness) => Math.max(0, Math.min(255, Math.round((Number(lightness) / 0xffff) * 255)));

/** Kelvin the lamp reports -> 0..255 coolness inside its range. */
export function toCoolness(kelvin, range) {
  const r = validRange(range) || DEFAULT_TEMP_RANGE;
  const k = Math.max(r.min, Math.min(r.max, Number(kelvin) || r.min));
  return Math.round(((k - r.min) * 255) / (r.max - r.min));
}

/** 0..255 coolness -> kelvin inside the lamp's range. */
export function toKelvin(coolness, range) {
  const r = validRange(range) || DEFAULT_TEMP_RANGE;
  return Math.round(r.min + ((r.max - r.min) * clamp255(coolness)) / 255);
}

export function validRange(range) {
  const min = Number(range?.min);
  const max = Number(range?.max);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  if (min < 800 || max > 20000 || min >= max) return null;
  return { min, max };
}

/**
 * The range a GROUP command should use: the overlap of every lamp's reported
 * range, so "coolest" is a temperature every lamp can actually show. Lamps that
 * never reported are skipped; none reported -> the spec range.
 */
export function commonRange(lamps) {
  let min = DEFAULT_TEMP_RANGE.min;
  let max = DEFAULT_TEMP_RANGE.max;
  let any = false;
  for (const l of lamps || []) {
    const r = validRange({ min: l?.MeshTempMin, max: l?.MeshTempMax });
    if (!r) continue; // eslint-disable-line no-continue
    if (!any) { min = r.min; max = r.max; any = true; } else { min = Math.max(min, r.min); max = Math.min(max, r.max); }
  }
  if (any && min >= max) {
    // Disjoint ranges: fall back to the first lamp's, rather than nonsense.
    const first = (lamps || []).map((l) => validRange({ min: l?.MeshTempMin, max: l?.MeshTempMax })).find(Boolean);
    return first;
  }
  return { min, max };
}

/** "brightness,coolness" -> [b, c]; unusable -> null. */
export function parseMeshValues(raw) {
  const parts = String(raw ?? '').split(',').map((x) => parseInt(x.trim(), 10));
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return null;
  return [clamp255(parts[0]), clamp255(parts[1])];
}

export const formatMeshValues = (brightness, coolness) => `${clamp255(brightness)},${clamp255(coolness)}`;
